import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from './config';
import { BlogPost, BlogPostInput, BlogPostStatus } from '../../types/blog';

const BLOG_COLLECTION = 'blog_posts';

const toDateOrNull = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  return null;
};

const mapDocToPost = (id: string, data: Record<string, unknown>): BlogPost => {
  return {
    id,
    title: String(data.title || ''),
    slug: String(data.slug || ''),
    excerpt: String(data.excerpt || ''),
    content: String(data.content || ''),
    status: (data.status as BlogPostStatus) || 'draft',
    publishAt: toDateOrNull(data.publishAt),
    publishedAt: toDateOrNull(data.publishedAt),
    authorId: String(data.authorId || ''),
    authorName: String(data.authorName || ''),
    tags: Array.isArray(data.tags) ? data.tags.map((tag) => String(tag)) : [],
    coverImageUrl: data.coverImageUrl ? String(data.coverImageUrl) : undefined,
    seoTitle: data.seoTitle ? String(data.seoTitle) : undefined,
    seoDescription: data.seoDescription ? String(data.seoDescription) : undefined,
    ogImageUrl: data.ogImageUrl ? String(data.ogImageUrl) : undefined,
    canonicalUrl: data.canonicalUrl ? String(data.canonicalUrl) : undefined,
    createdAt: toDateOrNull(data.createdAt) || undefined,
    updatedAt: toDateOrNull(data.updatedAt) || undefined,
  };
};

const toPostPayload = (input: BlogPostInput, authorId: string, authorName: string) => {
  const publishAtTimestamp = input.publishAt ? Timestamp.fromDate(input.publishAt) : null;

  return {
    title: input.title,
    slug: input.slug,
    excerpt: input.excerpt,
    content: input.content,
    status: input.status,
    publishAt: publishAtTimestamp,
    publishedAt: input.status === 'published' ? serverTimestamp() : null,
    authorId,
    authorName,
    tags: input.tags,
    coverImageUrl: input.coverImageUrl || null,
    seoTitle: input.seoTitle || null,
    seoDescription: input.seoDescription || null,
    ogImageUrl: input.ogImageUrl || null,
    canonicalUrl: input.canonicalUrl || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
};

export const blogService = {
  async getPublicPosts(limitCount = 20): Promise<BlogPost[]> {
    // Filter on status == 'published' to satisfy Firestore's query-rule
    // static analysis. Scheduled posts are not publicly visible until an
    // admin transitions them to 'published'.
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('status', '==', 'published'),
      orderBy('publishAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((document) =>
      mapDocToPost(document.id, document.data() as Record<string, unknown>)
    );
  },

  async getPublicPostBySlug(slug: string): Promise<BlogPost | null> {
    const q = query(
      collection(db, BLOG_COLLECTION),
      where('slug', '==', slug),
      where('status', '==', 'published'),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    return mapDocToPost(snapshot.docs[0].id, snapshot.docs[0].data() as Record<string, unknown>);
  },

  async getAdminPosts(limitCount = 100): Promise<BlogPost[]> {
    const q = query(collection(db, BLOG_COLLECTION), orderBy('updatedAt', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);

    return snapshot.docs.map((document) => mapDocToPost(document.id, document.data() as Record<string, unknown>));
  },

  async getPostById(id: string): Promise<BlogPost | null> {
    const documentRef = doc(db, BLOG_COLLECTION, id);
    const snapshot = await getDoc(documentRef);

    if (!snapshot.exists()) {
      return null;
    }

    return mapDocToPost(snapshot.id, snapshot.data() as Record<string, unknown>);
  },

  async createPost(input: BlogPostInput, authorId: string, authorName: string): Promise<string> {
    const payload = toPostPayload(input, authorId, authorName);
    const result = await addDoc(collection(db, BLOG_COLLECTION), payload);
    return result.id;
  },

  async updatePost(id: string, input: BlogPostInput): Promise<void> {
    const documentRef = doc(db, BLOG_COLLECTION, id);
    const publishAtTimestamp = input.publishAt ? Timestamp.fromDate(input.publishAt) : null;

    await updateDoc(documentRef, {
      title: input.title,
      slug: input.slug,
      excerpt: input.excerpt,
      content: input.content,
      status: input.status,
      publishAt: publishAtTimestamp,
      // Keep publishedAt stable if already set; if transitioning to published now, set timestamp.
      ...(input.status === 'published' ? { publishedAt: serverTimestamp() } : {}),
      tags: input.tags,
      coverImageUrl: input.coverImageUrl || null,
      seoTitle: input.seoTitle || null,
      seoDescription: input.seoDescription || null,
      ogImageUrl: input.ogImageUrl || null,
      canonicalUrl: input.canonicalUrl || null,
      updatedAt: serverTimestamp(),
    });
  },

  async slugExists(slug: string, excludeId?: string): Promise<boolean> {
    const q = query(collection(db, BLOG_COLLECTION), where('slug', '==', slug), limit(2));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return false;
    }

    if (!excludeId) {
      return true;
    }

    return snapshot.docs.some((document) => document.id !== excludeId);
  },
};
